import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiShield, FiArrowLeft, FiCheck, FiLock } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { check2FAStatus } from '../services/dataRoomService';
import TwoFactorSetup from '../components/TwoFactorSetup';

const DataRoom2FASetup: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [has2FA, setHas2FA] = useState<boolean | null>(null);
  const [showSetup, setShowSetup] = useState(false);

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    const enabled = await check2FAStatus();
    setHas2FA(enabled);
    if (enabled) {
      // Already has 2FA, redirect to data rooms list
      navigate('/data-rooms');
    }
  };

  const handleSuccess = () => {
    setShowSetup(false);
    setHas2FA(true);
    // Redirect to data rooms list after short delay
    setTimeout(() => {
      navigate('/data-rooms');
    }, 1500);
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <p className="text-gray-400">Please log in</p>
      </div>
    );
  }

  if (has2FA === null) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (has2FA) {
    return (
      <div className="min-h-screen bg-gray-950 pt-20 pb-12 px-4">
        <div className="max-w-lg mx-auto text-center">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/20 border border-green-500/30 flex items-center justify-center"
          >
            <FiCheck className="w-10 h-10 text-green-400" />
          </motion.div>
          <h1 className="text-2xl font-bold text-white mb-2">2FA Already Enabled</h1>
          <p className="text-gray-400 mb-6">
            Your account is secured with Two-Factor Authentication.
          </p>
          <button
            onClick={() => navigate('/data-rooms')}
            className="px-6 py-3 rounded-xl bg-gray-900/80 border border-cyan-500/20 hover:border-cyan-500/40 text-gray-300 hover:text-white font-medium transition-all"
          >
            Continue to Data Room
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 pt-20 pb-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <button
            onClick={() => navigate('/data-rooms')}
            className="flex items-center gap-2 text-gray-400 hover:text-white mb-4 transition-colors"
          >
            <FiArrowLeft className="w-4 h-4" />
            Back to Data Room
          </button>
        </motion.div>

        {/* Main Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center p-8 rounded-2xl bg-gray-900/50 border border-gray-800/50"
        >
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-purple-600/20 to-cyan-600/20 border border-purple-500/30 flex items-center justify-center">
            <FiShield className="w-10 h-10 text-cyan-400" />
          </div>

          <h1 className="text-2xl font-bold text-white mb-3">
            Enable Two-Factor Authentication
          </h1>
          <p className="text-gray-400 max-w-md mx-auto mb-8">
            To protect sensitive investor documents, you must enable Two-Factor Authentication 
            using Google Authenticator before creating or managing data rooms.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 text-left">
            <div className="p-4 rounded-xl bg-gray-800/50 border border-gray-700/50">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center mb-3">
                <span className="text-purple-400 font-bold">1</span>
              </div>
              <h3 className="text-white font-medium mb-1">Download App</h3>
              <p className="text-gray-500 text-sm">
                Install Google Authenticator on your phone
              </p>
            </div>
            <div className="p-4 rounded-xl bg-gray-800/50 border border-gray-700/50">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center mb-3">
                <span className="text-purple-400 font-bold">2</span>
              </div>
              <h3 className="text-white font-medium mb-1">Scan QR Code</h3>
              <p className="text-gray-500 text-sm">
                Link your account by scanning the QR code
              </p>
            </div>
            <div className="p-4 rounded-xl bg-gray-800/50 border border-gray-700/50">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center mb-3">
                <span className="text-purple-400 font-bold">3</span>
              </div>
              <h3 className="text-white font-medium mb-1">Verify Code</h3>
              <p className="text-gray-500 text-sm">
                Enter the 6-digit code to confirm setup
              </p>
            </div>
          </div>

          <button
            onClick={() => setShowSetup(true)}
            className="px-8 py-4 rounded-xl bg-gray-900/80 border border-cyan-500/20 hover:border-cyan-500/40 text-gray-300 hover:text-white font-medium transition-all flex items-center gap-3 mx-auto"
          >
            <FiLock className="w-5 h-5 text-cyan-400" />
            Set Up 2FA Now
          </button>

          <p className="text-gray-600 text-sm mt-6">
            This is required for all data room owners and administrators
          </p>
        </motion.div>
      </div>

      {/* 2FA Setup Modal */}
      {showSetup && user && (
        <TwoFactorSetup
          isOpen={showSetup}
          onClose={() => setShowSetup(false)}
          userEmail={user.email || ''}
          onSuccess={handleSuccess}
        />
      )}
    </div>
  );
};

export default DataRoom2FASetup;
